import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { PageShell } from '../layout/page-shell.jsx';
import { useToast } from '../ui/toast.jsx';
import { formatUtcInTimezone } from '../../lib/time.js';
import { api, ApiError } from '../../api/index.js';
import {
  LastRefresh, ExportButtons, SearchInput, applySearch,
  TtlBadge, useTableState, SortIcon,
} from '../../lib/table-utils.jsx';

/**
 * Intelligence — user-uploaded recon / situation overlays. Port of
 * reference `manuals/COP-microservice-integration/Intelligence/intelligence-page.jsx`.
 *
 * Reads layers from dataset "cop-ds-intel" (auto-created and fed by
 * AndroidIngestService). One layer per user (`cop-l-intel-{personUuid}`);
 * each upload from the same user OVERWRITES the previous content
 * (no historical clutter).
 *
 * v1.10 — the layerId suffix is the operator's persona UUID (canonical
 * transport identifier). The User column display resolves UUID → callsign
 * via the persons registry (api.persons.list) at render time. Eliminates
 * callsign collisions and keeps storage stable across callsign renames.
 * v1.9 incorrectly used callsign in the URL path; reverted in v1.10.
 *
 * Read-only operator view. Polls /api/cop-layers every 10s.
 *
 * Adjustments vs reference — see comment in cop-locations-page.jsx
 * (same set of coord-specific adaptations).
 */

const POLL_MS = 10000;
const INTEL_DATASET_ID = 'cop-ds-intel';

// Window for the per-user "freshness" badge. Intel is episodic — after
// 1 hour since the last upload the user is considered stale.
const FRESH_WINDOW_MS = 60 * 60 * 1000;

function userIdFromLayerId(layerId) {
  // Returns persona UUID (canonical id). Callsign resolution happens at
  // render time via the persons registry — see resolveCallsign() below.
  return String(layerId || '').replace(/^cop-l-intel-/, '') || '(unknown)';
}

/**
 * Resolve a persona UUID to the operator's current callsign via the
 * coord-side persons registry (already loaded by the page on mount).
 * Falls back to a short 8-char UUID prefix when the lookup misses —
 * happens when the operator was removed from coord but their intel
 * layer is still in ad-cop-service awaiting cleanup. Never crashes.
 */
function resolveCallsign(uuid, personsByUuid) {
  if (!uuid) return '(unknown)';
  const p = personsByUuid ? personsByUuid.get(String(uuid).toLowerCase()) : null;
  if (p && p.callsign) return p.callsign;
  // Fallback: short UUID prefix so the admin still has something to
  // grep audit logs by, instead of "(unknown)".
  return String(uuid).slice(0, 8);
}

function objectName(obj) {
  return obj.feature?.properties?.name
      || obj.operationalData?.unitName
      || obj.typeName
      || '';
}

/**
 * Detect whether a COP map-object has usable geometry. Codebase has TWO
 * shapes (see server: android-ingest.js / awds-layer-store.js / nato/adatp3
 * handlers):
 *
 *   - Marker internal:  { lat: number, lon: number, altitude?, rotation? }
 *   - GeoJSON:          { type, coordinates: [...] }   (Point/LineString/Polygon)
 *
 * Until v2.101.x the page only checked GeoJSON `coordinates` — markers
 * with `lat/lon` were undercounted. Affected the "With geometry" stat tile
 * and the per-object on-map indicator in the expanded user view.
 */
function hasGeometry(obj) {
  const g = obj?.geometry;
  if (!g) return false;
  if (Number.isFinite(g.lat) && Number.isFinite(g.lon)) return true;
  if (Array.isArray(g.coordinates) && g.coordinates.length > 0) return true;
  return false;
}

/**
 * Display label for the Geometry column. Normalises marker internal
 * format to "Point" so the column reads sensibly regardless of source.
 */
function geometryTypeLabel(obj) {
  const g = obj?.geometry;
  if (!g) return '';
  if (g.type) return g.type;
  if (Number.isFinite(g.lat) && Number.isFinite(g.lon)) return 'Point';
  return '';
}

export function CopIntelligencePage({ currentUser }) {
  const toast = useToast();
  const tz = currentUser?.timezone || 'UTC';
  const formatDate = useCallback((iso) => iso ? formatUtcInTimezone(iso, tz) : '—', [tz]);

  const [layers, setLayers] = useState([]);
  const [objectsByLayer, setObjectsByLayer] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedLayerId, setExpandedLayerId] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [search, setSearch] = useState('');
  const [notInstalled, setNotInstalled] = useState(null);
  // v1.10 — persona registry for UUID → callsign display lookup.
  // Loaded once on mount + on every poll cycle (cheap call, gives us
  // fresh callsign when admin renames an operator on Personnel page).
  const [personsByUuid, setPersonsByUuid] = useState(new Map());

  const reload = useCallback(async () => {
    try {
      // Fetch persons in parallel — independent call, no need to block
      // intel reload on registry failure. Build a lowercase-UUID map
      // since layer ids are lowercase but persons may have any case.
      const personsP = api.persons.list().then((r) => {
        const m = new Map();
        for (const p of (r?.persons || [])) {
          if (p && p.uuid) m.set(String(p.uuid).toLowerCase(), p);
        }
        setPersonsByUuid(m);
      }).catch(() => {});

      const all = await api.cop.getLayers();
      const list = all?.layers || all?.items || (Array.isArray(all) ? all : []);
      const items = list.filter((l) => l.datasetId === INTEL_DATASET_ID);
      items.sort((a, b) => userIdFromLayerId(a.id).localeCompare(userIdFromLayerId(b.id)));
      setLayers(items);
      await personsP;

      const objMap = {};
      await Promise.all(items.map(async (l) => {
        try {
          const res = await api.cop.getLayer(l.id);
          objMap[l.id] = res?.objects || [];
        } catch {
          objMap[l.id] = [];
        }
      }));
      setObjectsByLayer(objMap);
      setRefreshedAt(new Date());
      setNotInstalled(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        setNotInstalled(err.payload || { reason: 'unknown' });
      } else {
        toast.error(`Intelligence reload failed: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    const t = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      reload();
    }, POLL_MS);
    return () => clearInterval(t);
  }, [reload]);

  const flatRows = useMemo(() => {
    const rows = [];
    for (const layer of layers) {
      const layerUuid = userIdFromLayerId(layer.id);
      const userId = resolveCallsign(layerUuid, personsByUuid);
      const layerName = layer.name || '';
      const objs = objectsByLayer[layer.id] || [];
      for (const obj of objs) {
        const src = obj._androidSource || {};
        rows.push({
          userId,
          layerId: layer.id,
          layerName,
          objectId: obj.id,
          type: obj.typeId || obj.type || '',
          geometryType: geometryTypeLabel(obj),
          hasGeometry: hasGeometry(obj),
          name: objectName(obj),
          sentAt: src.sentAt || '',
          layerUpdatedAt: layer.updatedAt || '',
        });
      }
    }
    return rows;
  }, [layers, objectsByLayer, personsByUuid]);

  const searched = useMemo(
    () => applySearch(flatRows, search, ['userId', 'objectId', 'type', 'geometryType', 'name', 'layerName']),
    [flatRows, search]
  );

  const matchingLayerIds = useMemo(() => {
    if (!search) return null;
    return new Set(searched.map((r) => r.layerId));
  }, [searched, search]);

  const filteredLayers = useMemo(() => {
    if (!matchingLayerIds) return layers;
    return layers.filter((l) => matchingLayerIds.has(l.id));
  }, [layers, matchingLayerIds]);

  const filteredObjectsByLayer = useMemo(() => {
    if (!matchingLayerIds) return objectsByLayer;
    const matchingObjectIds = new Set(searched.map((r) => `${r.layerId}::${r.objectId}`));
    const out = {};
    for (const l of filteredLayers) {
      out[l.id] = (objectsByLayer[l.id] || []).filter((o) =>
        matchingObjectIds.has(`${l.id}::${o.id}`)
      );
    }
    return out;
  }, [filteredLayers, objectsByLayer, searched, matchingLayerIds]);

  const userSummaries = useMemo(() => filteredLayers.map((l) => ({
    layer: l,
    userId: resolveCallsign(userIdFromLayerId(l.id), personsByUuid),
    layerName: l.name || '',
    objects: (filteredObjectsByLayer[l.id] || []).length,
    lastUpload: l.updatedAt || '',
  })), [filteredLayers, filteredObjectsByLayer, personsByUuid]);

  const { sortedRows, sortKey, sortDir, toggleSort } = useTableState(userSummaries, {
    defaultSortKey: 'userId',
    defaultSortDir: 'asc',
    pageSize: 100,
    sortAccessors: {
      userId:     (r) => r.userId,
      layerName:  (r) => r.layerName,
      objects:    (r) => r.objects,
      lastUpload: (r) => r.lastUpload,
    },
  });

  const stats = useMemo(() => {
    let totalObjects = 0;
    let withGeometry = 0;
    for (const l of layers) {
      const objs = objectsByLayer[l.id] || [];
      totalObjects += objs.length;
      for (const o of objs) if (hasGeometry(o)) withGeometry++;
    }
    return { users: layers.length, totalObjects, withGeometry };
  }, [layers, objectsByLayer]);

  const exportColumns = [
    { key: 'userId',       label: 'User' },
    { key: 'layerName',    label: 'Layer name' },
    { key: 'objectId',     label: 'Object id' },
    { key: 'type',         label: 'Type' },
    { key: 'geometryType', label: 'Geometry' },
    { key: 'hasGeometry',  label: 'On map' },
    { key: 'name',         label: 'Name / label' },
    { key: 'sentAt',       label: 'Sent at (ISO)' },
  ];

  return (
    <PageShell
      icon="fa-binoculars"
      title="Intelligence"
      subtitle="User-uploaded reconnaissance overlays — intel layers from terminals via ad-tms-service. Each user has one layer; every upload overwrites the previous content."
      actions={
        notInstalled ? null : (
          <div className="flex items-center gap-2">
            <LastRefresh ts={refreshedAt} />
            <ExportButtons
              getRows={() => searched}
              columns={exportColumns}
              filenameBase="intelligence"
              disabled={searched.length === 0}
            />
          </div>
        )
      }
    >
      {notInstalled ? (
        <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <i className="fa-sharp fa-solid fa-triangle-exclamation mr-2" />
          COP ADMS service is not reachable
          {notInstalled.reason ? ` (${notInstalled.reason})` : ''}. Open
          <span className="font-medium"> COP ADMS → Datasets</span> for diagnostics.
        </div>
      ) : loading ? (
        <div className="card p-10 text-center text-sm text-mist">Loading intelligence…</div>
      ) : (
        <>
          <div className="mb-5 grid grid-cols-3 gap-3">
            <Stat label="Users"         value={stats.users} />
            <Stat label="Objects"       value={stats.totalObjects}  tone="amber" />
            <Stat label="With geometry" value={stats.withGeometry}  tone="emerald" />
          </div>

          <div className="mb-4 max-w-md">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search by user / object id / type / name…"
            />
          </div>

          <div className="overflow-hidden rounded-2xl border border-line bg-white shadow-card">
            <table className="w-full text-sm">
              <thead className="border-b border-line bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-mist">
                <tr>
                  <th className="px-3 py-2.5 w-10"></th>
                  <SortableTh label="User"        sortKey={sortKey} dir={sortDir} colKey="userId"     onSort={toggleSort} className="px-3 py-2.5" />
                  <SortableTh label="Layer name"  sortKey={sortKey} dir={sortDir} colKey="layerName"  onSort={toggleSort} className="px-3 py-2.5" />
                  <SortableTh label="Objects"     sortKey={sortKey} dir={sortDir} colKey="objects"    onSort={toggleSort} className="px-3 py-2.5 w-28 text-right" />
                  <SortableTh label="Last upload" sortKey={sortKey} dir={sortDir} colKey="lastUpload" onSort={toggleSort} className="px-3 py-2.5 w-44" />
                  <th className="px-3 py-2.5 w-52">Status</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.length === 0 ? (
                  <tr><td colSpan={6} className="p-8 text-center text-mist">
                    {search ? 'No users / objects match the search.' : 'No intel uploads yet. Waiting for user submissions via ad-tms-service.'}
                  </td></tr>
                ) : sortedRows.map(({ layer, userId, layerName, objects, lastUpload }) => {
                  const objs = filteredObjectsByLayer[layer.id] || [];
                  const expanded = expandedLayerId === layer.id;
                  return (
                    <Fragment key={layer.id}>
                      <tr
                        className="border-b border-line cursor-pointer hover:bg-slate-50"
                        onClick={() => setExpandedLayerId(expanded ? null : layer.id)}
                      >
                        <td className="px-3 py-2">
                          <i className={`fa-sharp fa-solid ${expanded ? 'fa-chevron-down' : 'fa-chevron-right'} text-xs text-mist`} />
                        </td>
                        <td className="px-3 py-2 font-mono text-xs font-semibold text-ink">{userId}</td>
                        <td className="px-3 py-2 text-xs">{layerName || '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-xs">{objects}</td>
                        <td className="px-3 py-2 text-xs text-mist tabular-nums">{lastUpload ? formatDate(lastUpload) : '—'}</td>
                        <td className="px-3 py-2"><TtlBadge lastUpdate={lastUpload || null} ttlMs={FRESH_WINDOW_MS} /></td>
                      </tr>
                      {expanded && (
                        <tr className="border-b border-line bg-slate-50/40">
                          <td colSpan={6} className="px-6 py-3">
                            {objs.length === 0 ? (
                              <div className="text-xs text-mist">Layer is empty.</div>
                            ) : (
                              <table className="w-full text-xs">
                                <thead className="text-mist text-[10px] uppercase tracking-wider">
                                  <tr>
                                    <th className="text-left py-1.5 pr-3">Object id</th>
                                    <th className="text-left py-1.5 pr-3">Type</th>
                                    <th className="text-left py-1.5 pr-3">Geometry</th>
                                    <th className="text-left py-1.5 pr-3">Name / label</th>
                                    <th className="text-left py-1.5 pr-3">Sent at</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {objs.map((obj) => {
                                    const src = obj._androidSource || {};
                                    const gtype = geometryTypeLabel(obj) || '—';
                                    const name = objectName(obj) || '—';
                                    const onMap = hasGeometry(obj);
                                    return (
                                      <tr key={obj.id} className="border-t border-line/60">
                                        <td className="py-1.5 pr-3 font-mono text-[11px]">{obj.id}</td>
                                        <td className="py-1.5 pr-3">{obj.typeId || obj.type || '—'}</td>
                                        <td className="py-1.5 pr-3">
                                          <span className={onMap ? 'text-emerald-700' : 'text-rose-600'} title={onMap ? 'On map' : 'No geometry → not on map'}>
                                            <i className={`fa-sharp fa-solid ${onMap ? 'fa-circle-check' : 'fa-circle-xmark'} mr-1 text-[10px]`} />
                                            {gtype}
                                          </span>
                                        </td>
                                        <td className="py-1.5 pr-3">{name}</td>
                                        <td className="py-1.5 pr-3 tabular-nums text-mist">{src.sentAt ? formatDate(src.sentAt) : '—'}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </PageShell>
  );
}

function SortableTh({ label, sortKey, dir, colKey, onSort, className = '' }) {
  const active = sortKey === colKey;
  return (
    <th className={`${className} cursor-pointer select-none whitespace-nowrap`} onClick={() => onSort(colKey)}>
      <span>{label}</span>
      <SortIcon active={active} dir={dir} />
    </th>
  );
}

function Stat({ label, value, tone }) {
  const cls = tone === 'emerald' ? 'text-emerald-700'
            : tone === 'amber'   ? 'text-amber-700'
            : 'text-ink';
  return (
    <div className="rounded-2xl border border-line bg-white px-4 py-3 shadow-card">
      <div className="text-xs uppercase tracking-wider text-mist">{label}</div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${cls}`}>{value}</div>
    </div>
  );
}
